import { Center, Group, Stack, Text } from '@mantine/core';
import type { ContextModalProps } from '@mantine/modals';

import { IconCheck, IconExternalLink } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '../UI/Button/Button';

import { useUpdateInteractionMutation } from '../../hooks/useUpdateInteractionMutation.hook';

type BuyerProfileSubscriptionModalProps = {
  buyerProfileParticipationURL?: string;
  tenderId: number;
};

export function BuyerProfileSubscriptionModal({
  context,
  id,
  innerProps,
}: ContextModalProps<BuyerProfileSubscriptionModalProps>) {
  const { t } = useTranslation('common');
  const { buyerProfileParticipationURL, tenderId } = innerProps;
  const updateInteractionMutation = useUpdateInteractionMutation();

  return (
    <Stack spacing="04">
      <Group noWrap spacing="04" align="flex-start">
        <Center
          miw="40px"
          mih="40px"
          bg="white"
          sx={theme => ({
            borderRadius: theme.radius.xl,
            border: `1px solid ${theme.colors.primary[2]}`,
          })}
          c="primary.6"
        >
          <IconCheck />
        </Center>
        <Stack spacing="01">
          <Text variant="md" fw={500} c="gray.9">
            {t('modals.buyerProfileSubscriptionModal.title')}
          </Text>
          <Text variant="sm" fw={400} c="gray.5">
            <Trans
              i18nKey="modals.buyerProfileSubscriptionModal.subtitle"
              components={{
                buyerProfileParticipationURL: buyerProfileParticipationURL ? (
                  <Link to={buyerProfileParticipationURL} target="_blank">
                    <Group
                      noWrap
                      spacing="00"
                      c="primary.7"
                      display="inline-flex"
                      sx={{ textDecoration: 'underline' }}
                    >
                      {t(
                        'modals.buyerProfileSubscriptionModal.buyerProfileParticipationURL',
                      )}
                      <IconExternalLink size={16} />
                    </Group>
                  </Link>
                ) : (
                  <span>
                    {t(
                      'modals.buyerProfileSubscriptionModal.buyerProfileParticipationURL',
                    )}
                  </span>
                ),
              }}
            />
          </Text>
        </Stack>
      </Group>

      <Group noWrap position="right" spacing="03">
        <Button
          variant="subtle"
          color="gray"
          onClick={() => {
            context.closeModal(id);
          }}
        >
          {t('ignore')}
        </Button>
        <Button
          size="md"
          onClick={() => {
            updateInteractionMutation.mutate({
              tenderId,
              payload: {
                isSubscribedToTenderUpdateOnBuyerProfile: true,
              },
            });
            context.closeModal(id);
          }}
        >
          {t('modals.buyerProfileSubscriptionModal.doneCTA')}
        </Button>
      </Group>
    </Stack>
  );
}
